import React, { useState } from 'react';
import { ComposableMap, Geographies, Geography } from 'react-simple-maps';
import { Tooltip } from 'react-tooltip';
import useI18n from '../../../hooks/useI18n';

interface CountryClicks {
  country: string;
  clicks: number;
}

interface WorldMapProps {
  countries: CountryClicks[];
  isDashboardPage?: boolean;
}

const WorldMap: React.FC<WorldMapProps> = ({
  countries,
  isDashboardPage = false,
}) => {
  const { t, currentLanguage } = useI18n();
  const [mousePos, setMousePos] = useState<{ x: number; y: number }>({
    x: 0,
    y: 0,
  });
  const [mapError, setMapError] = useState(false);

  const getClicksByCountryName = (iso: string): number => {
    const countryData = countries.find((c) => c.country === iso);
    return countryData ? countryData.clicks : 0;
  };

  const getLocalizedCountryName = (
    countryCode: string,
    englishName: string,
  ): string => {
    try {
      if (
        countryCode &&
        typeof countryCode === 'string' &&
        countryCode.length === 2
      ) {
        const regionNames = new Intl.DisplayNames([currentLanguage], {
          type: 'region',
        });
        return regionNames.of(countryCode) || englishName;
      }
    } catch (error) {
      console.error('Error getting localized country name', error);
    }
    return englishName;
  };

  const minClicks = 1;
  const maxClicks = countries.length
    ? Math.max(...countries.map((d) => d.clicks))
    : 5000;

  const colorScale = (clicks: number) => {
    if (clicks === 0) return '#f0f0f0';

    const logClicks = clicks > 0 ? Math.log10(clicks) : 0;
    const logMinClicks = minClicks > 0 ? Math.log10(minClicks) : 0;
    const logMaxClicks = maxClicks > 0 ? Math.log10(maxClicks) : 1;

    const denominator = logMaxClicks - logMinClicks || 1;
    const ratio = (logClicks - logMinClicks) / denominator;

    if (ratio >= 0.8) return '#30009C';
    if (ratio >= 0.6) return '#5000B9';
    if (ratio >= 0.4) return '#7700D6';
    if (ratio >= 0.2) return '#AB4DE9';
    return '#ECD8FD';
  };

  const handleGeoError = () => {
    console.error('Error loading geography data');
    setMapError(true);
  };

  if (mapError) {
    return (
      <div className="flex items-center justify-center w-full h-full border border-gray-200 bg-gray-50 rounded-md">
        <p className="text-gray-500">La carte n&apos;a pas pu être chargée.</p>
      </div>
    );
  }

  if (!countries || countries.length === 0) {
    return (
      <div className="flex items-center justify-center w-full h-full border border-gray-200 bg-gray-50 rounded-md">
        <p className="text-gray-500">{String(t('common.noData'))}</p>
      </div>
    );
  }

  return (
    <>
      <ComposableMap
        projection="geoMercator"
        projectionConfig={{
          scale: 105,
          center: [0, 0],
        }}
      >
        <Geographies
          geography="/data/countries-110m.json"
          onError={handleGeoError}
        >
          {({ geographies }) =>
            geographies.map((geo) => {
              const countryCode = geo.properties.ISO_A2_EH;
              const clicks = getClicksByCountryName(countryCode);

              return (
                <Geography
                  className="country"
                  key={geo.rsmKey}
                  geography={geo}
                  fill={clicks > 0 ? colorScale(clicks) : '#f0f0f0'}
                  stroke="#FFFFFF"
                  strokeWidth={0.5}
                  data-tooltip-content={`${getLocalizedCountryName(countryCode, geo.properties.NAME_EN)}: ${clicks} ${String(t('analytics.clicks'))}`}
                  onMouseMove={(event) => {
                    setMousePos({
                      x: event.clientX,
                      y: event.clientY,
                    });
                  }}
                  style={{
                    default: { outline: 'none' },
                    hover: { outline: 'none' },
                    pressed: { outline: 'none' },
                  }}
                />
              );
            })
          }
        </Geographies>
      </ComposableMap>

      <div className="hidden sm:block absolute bottom-0 left-4">
        <div className={`relative ${isDashboardPage ? 'w-64' : 'w-96'}`}>
          <div className="h-3 w-full rounded-md bg-gradient-to-r from-[#ECD8FD] via-[#7700D6] to-[#30009C]"></div>
          <div className="flex justify-between mt-1">
            <span className="text-xs text-gray-600">
              {Number.isFinite(minClicks) ? minClicks : '1'}
            </span>
            <span className="text-xs text-gray-600">
              {Number.isFinite(maxClicks) ? maxClicks : '5000'}
            </span>
          </div>
        </div>
      </div>

      <div className="sm:hidden absolute bottom-0 left-0 right-0">
        <div className="relative">
          <div className="h-3 w-full rounded-md bg-gradient-to-r from-[#ECD8FD] via-[#7700D6] to-[#30009C]"></div>
          <div className="flex justify-between mt-1">
            <span className="text-xs text-gray-600">
              {Number.isFinite(minClicks) ? minClicks : '1'}
            </span>
            <span className="text-xs text-gray-600">
              {Number.isFinite(maxClicks) ? maxClicks : '5000'}
            </span>
          </div>
        </div>
      </div>

      <Tooltip anchorSelect=".country" position={mousePos} />
    </>
  );
};

export default WorldMap;
